import React, {useState, useEffect, useMemo} from 'react';
import {
  Box,
  Grid,
  Typography,
  useTheme,
  CircularProgress,
  MenuItem,
  FormControl,
  Select,
} from '@mui/material';
import {getWeatherByLatLon} from 'src/apis/config/axiosConfigNoCredential';
import chanceFlurries from 'src/assets/weather_icons/chanceflurries@3x.png';
import chanceRain from 'src/assets/weather_icons/chancerain@3x.png';
import chanceSleet from 'src/assets/weather_icons/chancesleet@3x.png';
import chanceStorms from 'src/assets/weather_icons/chancetstorms@3x.png';
import clear from 'src/assets/weather_icons/clear@3x.png';
import fog from 'src/assets/weather_icons/fog@3x.png';
import mostlyCloudy from 'src/assets/weather_icons/mostlycloudy@3x.png';
import partlySunny from 'src/assets/weather_icons/partlysunny@3x.png';
import rain from 'src/assets/weather_icons/rain@3x.png';
import sleet from 'src/assets/weather_icons/sleet@3x.png';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import LocalDB from 'src/utils/localDB';
import {Apartment} from 'src/apis/autoGenerate/types';

dayjs.extend(utc);
dayjs.extend(timezone);

// Define the Geo type needed for props (or import if defined elsewhere)
export interface Geo {
  lat: number;
  lng: number;
}

// Local storage key for weather property preference
const WEATHER_PROPERTY_PREFERENCE_KEY = 'weatherPropertyId';

// Weather icon mapping function
function getWeatherIcon(condition: string): string {
  // Convert condition to lowercase for easier comparison
  const lowerCondition = condition.toLowerCase();

  if (lowerCondition.includes('flurries')) {
    return chanceFlurries;
  } else if (lowerCondition.includes('chance') && lowerCondition.includes('rain')) {
    return chanceRain;
  } else if (lowerCondition.includes('chance') && lowerCondition.includes('sleet')) {
    return chanceSleet;
  } else if (
    lowerCondition.includes('chance') &&
    (lowerCondition.includes('storm') || lowerCondition.includes('thunder'))
  ) {
    return chanceStorms;
  } else if (lowerCondition.includes('fog') || lowerCondition.includes('mist')) {
    return fog;
  } else if (lowerCondition.includes('cloud') || lowerCondition.includes('overcast')) {
    return mostlyCloudy;
  } else if (
    lowerCondition.includes('partly') &&
    (lowerCondition.includes('sunny') || lowerCondition.includes('cloud'))
  ) {
    return partlySunny;
  } else if (lowerCondition.includes('rain') || lowerCondition.includes('shower')) {
    return rain;
  } else if (lowerCondition.includes('sleet') || lowerCondition.includes('freezing rain')) {
    return sleet;
  } else if (lowerCondition.includes('clear') || lowerCondition.includes('sunny')) {
    return clear;
  } else {
    // Default icon if no match
    return clear;
  }
}

interface WeatherCardProps {
  apartments?: Apartment[]; // Array of apartments to choose from
  deviceList?: Array<{apartmentId?: string; timeZone?: string}>; // Device list for timezone lookup
  // Legacy props (for backward compatibility)
  position?: Geo;
  locationName?: string;
  isDefaultLocation?: boolean;
  timeZone?: string;
}

export default function WeatherCard({
  apartments,
  deviceList,
  position: legacyPosition,
  locationName: legacyLocationName,
  isDefaultLocation: legacyIsDefaultLocation,
  timeZone: legacyTimeZone,
}: WeatherCardProps) {
  const theme = useTheme();
  const [todayWeather, setTodayWeather] = useState({
    shortForecast: '',
    temperature: '',
    temperatureUnit: '',
    icon: '',
    windSpeed: '',
  });
  const [isLoading, setIsLoading] = useState(true);

  // Get apartments with valid locations
  const validApartments = useMemo(() => {
    if (!apartments || !Array.isArray(apartments)) {
      return [];
    }
    return apartments.filter(
      apt =>
        apt.location &&
        typeof apt.location.latitude === 'number' &&
        typeof apt.location.longitude === 'number'
    );
  }, [apartments]);

  // Get saved preference or default to first apartment
  const [selectedApartmentId, setSelectedApartmentId] = useState<string | null>(() => {
    const saved = LocalDB.get(WEATHER_PROPERTY_PREFERENCE_KEY);
    if (saved && validApartments.some(apt => apt.id === saved)) {
      return saved;
    }
    return validApartments.length > 0 ? validApartments[0].id || null : null;
  });

  // Update selected apartment when valid apartments change
  useEffect(() => {
    if (validApartments.length > 0) {
      const saved = LocalDB.get(WEATHER_PROPERTY_PREFERENCE_KEY);
      const savedApartment = validApartments.find(apt => apt.id === saved);
      if (savedApartment) {
        setSelectedApartmentId(savedApartment.id || null);
      } else if (
        !selectedApartmentId ||
        !validApartments.find(apt => apt.id === selectedApartmentId)
      ) {
        // If current selection is invalid, use first apartment
        setSelectedApartmentId(validApartments[0].id || null);
      }
    }
  }, [validApartments, selectedApartmentId]);

  // Get selected apartment
  const selectedApartment = useMemo(() => {
    if (!selectedApartmentId) return null;
    return validApartments.find(apt => apt.id === selectedApartmentId) || null;
  }, [selectedApartmentId, validApartments]);

  // Determine position, location name, and timezone
  const LA_CENTER = useMemo(() => ({lat: 34.0522, lng: -118.2437}), []);
  const weatherPosition = useMemo(() => {
    if (selectedApartment?.location) {
      return {
        lat: selectedApartment.location.latitude!,
        lng: selectedApartment.location.longitude!,
      };
    }
    // Fallback to legacy position or LA
    return legacyPosition || LA_CENTER;
  }, [selectedApartment, legacyPosition, LA_CENTER]);

  const locationName = useMemo(() => {
    if (selectedApartment?.city && selectedApartment?.state) {
      return `${selectedApartment.city}, ${selectedApartment.state}`;
    }
    if (selectedApartment?.name) {
      return selectedApartment.name;
    }
    return legacyLocationName || 'Los Angeles';
  }, [selectedApartment, legacyLocationName]);

  const isDefaultLocation = useMemo(() => {
    return validApartments.length === 0 || !selectedApartment;
  }, [validApartments.length, selectedApartment]);

  // Get timezone for selected apartment
  const apartmentTimeZone = useMemo(() => {
    if (!selectedApartment) {
      return legacyTimeZone || 'America/Los_Angeles';
    }

    // Try to get timezone from devices in this apartment
    if (deviceList && deviceList.length > 0) {
      const apartmentDevices = deviceList.filter(device => {
        return device.apartmentId === selectedApartment.id;
      });

      if (apartmentDevices.length > 0 && apartmentDevices[0].timeZone) {
        return apartmentDevices[0].timeZone;
      }
    }

    // Fallback: Infer timezone from coordinates
    const {latitude, longitude} = selectedApartment.location || {};
    if (typeof latitude === 'number' && typeof longitude === 'number') {
      if (longitude >= -84 && longitude < -72) return 'America/New_York';
      if (longitude >= -102 && longitude < -84) return 'America/Chicago';
      if (longitude >= -115 && longitude < -102) return 'America/Denver';
      if (longitude >= -125 && longitude < -115) return 'America/Los_Angeles';
      if (longitude < -125) return 'America/Anchorage';
      if (longitude >= -72) return 'America/Halifax';
    }

    return legacyTimeZone || 'America/Los_Angeles';
  }, [selectedApartment, deviceList, legacyTimeZone]);

  // Handle property selection change
  const handlePropertyChange = (apartmentId: string) => {
    setSelectedApartmentId(apartmentId);
    LocalDB.set(WEATHER_PROPERTY_PREFERENCE_KEY, apartmentId);
  };

  useEffect(() => {
    setIsLoading(true);
    if (
      weatherPosition &&
      typeof weatherPosition.lat === 'number' &&
      typeof weatherPosition.lng === 'number'
    ) {
      getWeatherByLatLon(weatherPosition.lat, weatherPosition.lng)
        .then(data => {
          if (data?.data?.properties?.periods) {
            const periods = data.data.properties.periods;

            // Get current time in apartment's timezone
            const now = dayjs().tz(apartmentTimeZone);

            // Find the period that contains the current time
            let selectedPeriod = periods[0]; // Default to first period

            for (const period of periods) {
              const startTime = dayjs(period.startTime);
              const endTime = dayjs(period.endTime);

              // Check if current time falls within this period's time range
              if (now.isAfter(startTime) && now.isBefore(endTime)) {
                selectedPeriod = period;
                break;
              }
            }

            // If no current period found (current time not in any range), find the next upcoming period
            if (selectedPeriod === periods[0]) {
              for (const period of periods) {
                const startTime = dayjs(period.startTime);
                if (startTime.isAfter(now)) {
                  selectedPeriod = period;
                  break;
                }
              }
            }

            setTodayWeather(selectedPeriod);
          }
        })
        .catch(error => {
          console.error('[WeatherCard] Weather API error:', error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, [weatherPosition, apartmentTimeZone]);

  const DAY = useMemo(() => {
    return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  }, []);

  const today = new Date();

  const weatherIcon = useMemo(() => {
    return getWeatherIcon(todayWeather.shortForecast);
  }, [todayWeather.shortForecast]);

  return (
    <Box
      sx={{
        p: {xs: 2, sm: 2.5},
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {validApartments.length > 1 && (
        <Box sx={{display: 'flex', justifyContent: 'flex-end', mb: 1}}>
          <FormControl size="small" sx={{minWidth: {xs: 120, sm: 150}}}>
            <Select
              value={selectedApartmentId || ''}
              onChange={e => handlePropertyChange(e.target.value as string)}
              sx={{
                fontSize: {xs: 11, sm: 12},
                height: {xs: 28, sm: 32},
                '& .MuiSelect-select': {
                  py: {xs: 0.5, sm: 1},
                },
              }}
            >
              {validApartments.map(apt => (
                <MenuItem key={apt.id} value={apt.id || ''}>
                  <Typography
                    sx={{
                      fontSize: {xs: 11, sm: 12},
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: {xs: 100, sm: 130},
                    }}
                  >
                    {apt.name || `${apt.city || ''}, ${apt.state || ''}`.trim() || 'Property'}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      <Grid
        container
        spacing={{xs: 1, sm: 2}} // Adjust spacing between columns
        alignItems="center" // Vertically align items in the grid
        sx={{width: '100%', flex: 1}}
      >
        {/* Column 1: Date & Location */}
        <Grid item xs={4}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              ml: {xs: 1, sm: 2}, // Add margin-left here instead
            }}
          >
            <Typography
              sx={{
                fontSize: {xs: 13, sm: 14},
                color: theme.palette.text.secondary,
              }}
            >
              {DAY[today.getDay()]}
            </Typography>
            <Typography
              sx={{
                fontSize: {xs: 32, sm: 40}, // Larger date
                fontWeight: 600,
                lineHeight: 1.1,
                my: {xs: 0.5, sm: 1}, // Spacing around date
              }}
            >
              {today.getDate()}
            </Typography>
            <Typography
              sx={{
                fontSize: {xs: 13, sm: 14},
                fontWeight: 500,
                lineHeight: 1.2,
              }}
            >
              {locationName}
            </Typography>
            {isDefaultLocation && (
              <Typography
                sx={{
                  fontSize: {xs: 11, sm: 12},
                  color: theme.palette.text.secondary,
                  mt: 0.5,
                }}
              >
                No apartment added yet — showing weather for default location (Los Angeles).
              </Typography>
            )}
          </Box>
        </Grid>

        {/* Column 2: Weather Icon */}
        <Grid item xs={4}>
          <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
            {isLoading ? (
              <CircularProgress size={56} /> // Larger loading indicator
            ) : (
              <img
                src={weatherIcon}
                alt={todayWeather.shortForecast}
                style={{
                  width: '96px', // Match mockup icon size
                  height: '96px',
                  objectFit: 'contain',
                }}
              />
            )}
          </Box>
        </Grid>

        {/* Column 3: Temperature & Details */}
        <Grid item xs={4}>
          <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start'}}>
            <Typography
              sx={{
                fontSize: {xs: 32, sm: 40}, // Larger temperature
                fontWeight: 600,
                lineHeight: 1.1,
                mb: {xs: 0.5, sm: 1}, // Spacing below temp
              }}
            >
              {todayWeather.temperature
                ? `${todayWeather.temperature}°${todayWeather.temperatureUnit}`
                : '--'}
            </Typography>
            <Typography
              sx={{
                fontSize: {xs: 13, sm: 14}, // Condition font size
                fontWeight: 500,
                lineHeight: 1.2,
                mb: 0.5, // Spacing below condition
              }}
            >
              {todayWeather.shortForecast || 'Loading...'}
            </Typography>
            <Typography
              sx={{
                fontSize: {xs: 11, sm: 12}, // Wind speed font size
                color: theme.palette.text.secondary,
                lineHeight: 1.2,
              }}
            >
              Wind Speed: {todayWeather.windSpeed || 'N/A'}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
